// Accelerator Types

export type CohortStatus = 'planned' | 'active' | 'closed';
export type StartupStatus = 'active' | 'at_risk' | 'complete' | 'paused';
export type AppRole = 'admin' | 'program_manager' | 'founder';

export interface Cohort {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: CohortStatus;
  created_at: string;
  updated_at: string;
}

export interface AcceleratorStartup {
  id: string;
  scout_card_id: string;
  cohort_id: string | null;
  founder_user_id: string | null;
  current_week: number;
  completion_percent: number;
  status: StartupStatus;
  intake_responses: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Joined data
  scout_card?: {
    id: string;
    company_name: string;
    short_summary: string;
    primary_contact_name: string;
    primary_contact_email: string;
    technology_category: string;
    company_stage: string;
    logo_url: string | null;
  };
  cohort?: Cohort;
  accelerator_scout_card?: AcceleratorScoutCard;
}

export interface AcceleratorScoutCard {
  id: string;
  startup_id: string;
  elevator_pitch: string | null;
  dual_use_fit_score: number | null;
  commercial_readiness: string | null;
  market_readiness: string | null;
  internal_notes: string | null;
  last_updated_by: string | null;
  updated_at: string;
  finalized_data: unknown | null;
  finalized_at: string | null;
  finalized_by: string | null;
}

export interface AcceleratorModule {
  id: string;
  week_number: number;
  title: string;
  overview_text: string | null;
  content_blocks: unknown[];
  required_text_fields: unknown[];
  required_uploads: unknown[];
  video_url: string | null;
  is_active: boolean;
  estimated_hours?: number;
  created_at: string;
  updated_at: string;

}

export interface AcceleratorSubmission {
  id: string;
  startup_id: string;
  week_number: number;
  text_responses: Record<string, unknown>;
  is_complete: boolean;
  submitted_at: string | null;
  unlocked_by: string | null;
  created_at: string;
  updated_at: string;
  /** Set by DB trigger when a completed week is reopened; cleared on resubmit. */
  reopened_at?: string | null;
  /** Snapshot of text_responses at the moment of reopen; cleared on resubmit. */
  reopened_snapshot?: Record<string, unknown> | null;
}

export interface AcceleratorSubmissionFile {
  id: string;
  submission_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number | null;
  created_at: string;
}

export interface FounderInvite {
  id: string;
  email: string;
  startup_id: string;
  invited_by: string | null;
  status: string;
  created_at: string;
}

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  created_at: string;
}

// Chalk groupings
export interface ChalkGroup {
  label: string;
  subtitle: string;
  weeks: number[];
}

export const CHALK_GROUPS: ChalkGroup[] = [
  { label: 'Orientation', subtitle: 'Orientation & My Pillars', weeks: [0] },
  { label: 'Chalk 1', subtitle: 'Product Market Fit & Timing', weeks: [1, 2, 3, 4, 5] },
  { label: 'Chalk 2', subtitle: 'Organizational Planning & Strategy', weeks: [6, 7, 8, 9, 10] },
  { label: 'Chalk 3', subtitle: 'Financial Model & Capitalization Planning', weeks: [11, 12, 13, 14, 15] },
];

export function getChalkForWeek(week: number): ChalkGroup | undefined {
  return CHALK_GROUPS.find(g => g.weeks.includes(week));
}

// Module curriculum titles
export const MODULE_TITLES: Record<number, string> = {
  0: 'Orientation',
  1: 'Program Kickoff & Startup Onboarding',
  2: 'Lean Canvas & Problem/Solution Validation',
  3: 'Customer Discovery & Adoption Risk',
  4: 'Competitive Landscape & Differentiation',
  5: 'White Paper Development & Market Validation',
  6: 'Organizational Readiness & Team Capabilities',
  7: 'Licensing, Regulatory, and IP Strategy',
  8: 'Channel Partner & Support Vendor Assessment',
  9: 'Commercial Market Entry Strategy',
  10: 'Government Market Entry Strategy',
  11: 'Budgets, Financial Projections & Startup Accounting',
  12: 'Key Performance Indicators (KPIs) & Growth Metrics',
  13: 'Capitalization Strategy & Fundraising Readiness',
  14: 'Investor Pitch & Terms Preparation',
  15: 'Final Investment Decision & Next Steps',
};

export const getStatusColor = (status: StartupStatus): string => {
  const colors: Record<StartupStatus, string> = {
    active: 'bg-green-100 text-green-800',
    at_risk: 'bg-amber-100 text-amber-800',
    complete: 'bg-blue-100 text-blue-800',
    paused: 'bg-gray-100 text-gray-800',
  };
  return colors[status];
};

export const getCohortStatusColor = (status: CohortStatus): string => {
  const colors: Record<CohortStatus, string> = {
    planned: 'bg-purple-100 text-purple-800',
    active: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };
  return colors[status];
};
